import { useEffect } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import connectionMonitor from "../../../utils/connectionMonitor";
import { getAuthToken } from "../../../utils/getAuthToken";
import useCallingAppEnabled from "./useCallingAppEnabled";
import {
  liveCallsHydrated,
  liveCallUpserted,
  liveCallClaimed,
  liveCallAnswered,
  liveCallEnded,
  liveCallTransferred,
} from "../../../redux/Slice/Calls/liveCallsSlice";

// Calling has its own dedicated single-instance service — falls back to the
// main API server only if REACT_APP_CALLING_URL is unset (e.g. not yet configured).
const SERVER_DOMAIN = process.env.REACT_APP_CALLING_URL || process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

/**
 * Mount once, near the app root (see CallManager) — not per-visibility of the
 * Calls sidebar section, so the "N live" badge stays accurate even while an
 * agent is looking at Chats. Hydrates once via REST, then only ever patches
 * from socket events; never polls or re-fetches.
 */
export default function useLiveCallsFeed() {
  const dispatch = useDispatch();
  // Cheap, shared "is calling on at all" check (see useCallingAppEnabled's own
  // doc comment). Defaults to true while still loading, so this doesn't delay
  // the initial GET for the common (enabled) case — only skips it once the
  // check has actually come back false.
  const { enabled: callingAppEnabled } = useCallingAppEnabled();

  // A single coordinated effect, not two independent ones: socket listeners
  // used to attach in a separate effect from the initial GET, which meant
  // live events could start arriving (and dispatching straight to the store)
  // before the GET's — by-then-stale — snapshot landed and blindly replaced
  // the whole byId map. That silently discarded whichever side lost the
  // race: a call that started ringing in that window would vanish again the
  // instant the snapshot arrived, or a call that had already ended could get
  // resurrected by a snapshot taken before it did. Buffering every event
  // until the snapshot is in hand, then replaying them in their original
  // order on top of it, gets the same end state regardless of which one
  // actually arrives first.
  useEffect(() => {
    let cancelled = false;
    let hydrated = false;
    const buffered = [];
    const dispatchOrBuffer = (action) => {
      if (hydrated) dispatch(action);
      else buffered.push(action);
    };

    const onLiveUpdate = (payload) => dispatchOrBuffer(liveCallUpserted(payload));
    const onClaimed = (payload) => dispatchOrBuffer(liveCallClaimed(payload));
    const onAnswered = (payload) => dispatchOrBuffer(liveCallAnswered(payload));
    const onEnded = (payload) => dispatchOrBuffer(liveCallEnded(payload));
    const onTransferred = (payload) => dispatchOrBuffer(liveCallTransferred(payload));

    connectionMonitor.on("call:live_update", onLiveUpdate);
    connectionMonitor.on("call:claimed", onClaimed);
    connectionMonitor.on("call:answered", onAnswered);
    connectionMonitor.on("call:ended", onEnded);
    connectionMonitor.on("call:transferCompleted", onTransferred);

    (async () => {
      try {
        if (!callingAppEnabled) {
          // Calling's turned off for this workspace — skip the GET entirely
          // instead of firing it and having it denied by roleauth("Calls.View")
          // for every team member without that permission, once per session.
          if (!cancelled) dispatch(liveCallsHydrated([]));
          return;
        }
        const token = await getAuthToken();
        if (!token) return;
        const res = await axios.get(`${SERVER_DOMAIN}/api/calling/live`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) dispatch(liveCallsHydrated(res.data?.data || []));
      } catch (err) {
        // best-effort — the sidebar just opens empty; the buffered/future
        // socket events (flushed below regardless of success) still
        // populate it from here on
      } finally {
        if (!cancelled) {
          hydrated = true;
          buffered.forEach((action) => dispatch(action));
          buffered.length = 0;
        }
      }
    })();

    return () => {
      cancelled = true;
      connectionMonitor.off("call:live_update", onLiveUpdate);
      connectionMonitor.off("call:claimed", onClaimed);
      connectionMonitor.off("call:answered", onAnswered);
      connectionMonitor.off("call:ended", onEnded);
      connectionMonitor.off("call:transferCompleted", onTransferred);
    };
  }, [dispatch, callingAppEnabled]);
}
